import "./management-modal.scss";

import * as React from "react";

import {
    faAddressCard,
    faArrowUpRightFromSquare,
    faBuilding,
    faShieldHalved,
    faTruck,
    faUsers,
}                           from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon}    from "@fortawesome/react-fontawesome";
import {cx}                 from "emotion";

import {cst, cstAssign}     from "@Common/react/chain-services";
import {consumeContext}     from "@Common/react/context";
import {Load2}              from "@Common/react/load2";
import {Static2}            from "@Common/react/static-2";
import {UseState}           from "@Common/react/use-state";
import {Responsive}         from "@Common/ui-components/responsive";
import {Types}              from "@Common/types";
import {ConfirmDialog}      from "@Common/ui-components/heavy/common/design2/dialogs/confirm-dialog/confirm-dialog";
import {ModalService3}      from "@Common/ui-components/modal-service-3/modal-service-3";
import {needsConfirmation}  from "@Common/ui-components/needs-confirmation/needs-confirmation";
import {oMapToArr}          from "@Common/utils/objects";
import {InternalLink}       from "@Xpresso/routes/common/components/internal-link";
import {AddressEdit}        from "./address-edit/address-edit";

import {ContactTab}  from "./contact/contact-tab";
import {GeneralTab}  from "./general/general-tab";
import {SecurityTab} from "./security/security-tab";
import {ShippingTab} from "./shipping/shipping-tab";

type TabName           = Types.Xpresso.Account.Admin.OfficeManagement.ManagementModal.TabName;
type TabComponentProps = Types.Xpresso.Account.Admin.OfficeManagement.ManagementModal.TabComponentProps;

const TAB_COMPONENTS:{[key in TabName]: (props:TabComponentProps) => React.FunctionComponentElement<TabComponentProps>} = {
    contact  : ContactTab,
    general  : GeneralTab,
    shipping : ShippingTab,
    security : SecurityTab,
};

const TAB_LINKS:Array<{name: TabName, label: string, icon: any}> = [
    {name: "general", label: "General Info", icon: faBuilding},
    {name: "contact", label: "Contact Info", icon: faAddressCard},
    {name: "shipping", label: "Shipping Info", icon: faTruck},
    {name: "security", label: "Security", icon: faShieldHalved},
];

export const ManagementModal = ({branchId, onClose: emitClose, onTabSwitch: emitTabSwitch, onSave: emitSave, show, tabName: activeTabName, officesReload}:{
    branchId         : string,
    onClose         ?: () => void,
    onSave          ?: (office:Types.Apis.Payloads.Next.Branch.GetById.Response) => void,
    onTabSwitch     ?: (tab:TabName) => void,
    show             : boolean,
    tabName          : TabName,
    officesReload   ?: () => void,
}) => cst<{
    apis                    : Types.Apis.Loaders.XpressoLoadApis,
    branchLoad              : Types.Common.React.Load2.Load2Next<Types.Apis.Payloads.Next.Branch.GetById.Response>,
    disabledState           : Types.Common.React.UseState.UseStateNext<boolean>,
    exitConfirmationModal   : Types.Common.UiComponents.ModalService3.ModalService3RootNext<boolean, boolean>,
    hasUnsavedChangesStatic : Types.Common.React.Static2.Static2Next<boolean>,
    mainModal               : Types.Common.UiComponents.ModalService3.ModalService3Next<boolean>,
    resetTabs               : () => void,
    responsive              : any,
    safeAction              : (action: () => void) => void,
    safeLinkClickHandler    : () => Promise<boolean>,
    tabResetCallbacksStatic : Types.Common.React.Static2.Static2Next<{[name in TabName]?: () => void}>,
}>(
    consumeContext("apis"),

    cstAssign("tabResetCallbacksStatic", ({}, next) => Static2({next, getInitValue: () => ({})})),
    cstAssign("resetTabs", ({tabResetCallbacksStatic: {get: getTabResetCallbacks}}, next) => next(
        () => oMapToArr(getTabResetCallbacks()).forEach((resetTab) => resetTab?.())
    )),

    cstAssign("hasUnsavedChangesStatic", ({}, next) => Static2({next, getInitValue: () => false})),
    cstAssign("exitConfirmationModal", ({}, next) => ConfirmDialog({next})),
    cstAssign("safeAction", ({
        exitConfirmationModal,
        hasUnsavedChangesStatic: {get: getHasUnsavedChanges, set: setHasUnsavedChanges},
        resetTabs,
    }, next) => (
        next(async (action) => {
            const confirmed = !getHasUnsavedChanges() || await needsConfirmation({
                confirmDialog: exitConfirmationModal
            });
            if (confirmed) {
                action();
                resetTabs();
                setHasUnsavedChanges(false);
            }
        })
    )),
    cstAssign("safeLinkClickHandler", ({
        exitConfirmationModal,
        hasUnsavedChangesStatic: {get: getHasUnsavedChanges},
    }, next) => (
        next(async () => !getHasUnsavedChanges() || await needsConfirmation({
            confirmDialog: exitConfirmationModal
        }))
    )),

    cstAssign("branchLoad", ({apis, hasUnsavedChangesStatic: {set: setHasUnsavedChanges}, resetTabs}, next) => (
        Load2({
            next,
            _key   : branchId,
            fetch  : () => branchId ? apis.next.getBranchById(branchId) : Promise.resolve(null),
            onLoad : () => {
                setHasUnsavedChanges(false);
                resetTabs();
            },
        })
    )),
    cstAssign("disabledState", ({}, next) => UseState({next, initValue: false})),

    cstAssign("mainModal", ({safeAction}, next) => (
        ModalService3({
            next,
            onClose     : async () => safeAction(emitClose),
            rootNext    : () => false,
            state       : {
                onChange : () => {},
                value    : {showing: branchId && show},
            },
            width       : 536,
            xToClose    : false,
            xdQaName    : "Management Modal",
        })
    )),
    // @ts-ignore
    cstAssign("responsive", (_, next) => <Responsive {...{
        sizes: {
            md: 1024,
        },
        next,
    }}/>),
    ({
        apis,
        branchLoad               : {loading, reload, value: branch},
        disabledState            : {onChange: setDisabled, value: disabled},
        hasUnsavedChangesStatic,
        mainModal                : {close: closeModal},
        responsive,
        safeAction,
        safeLinkClickHandler     : handleLinkClick,
        tabResetCallbacksStatic  : {get: getTabResetCallbacks, set: setTabResetCallbacks},
    }) => (
        <div className="management-modal-of15">
            {
                responsive.ge("md") ? (
                    headerDesktop({activeTabName, apis, branch, branchId, closeModal, disabled, emitTabSwitch, handleLinkClick, loading, reload, officesReload, safeAction, setDisabled})
                ) : (
                    headerMobile({activeTabName, apis, branch, branchId, closeModal, disabled, emitTabSwitch, handleLinkClick, loading, reload, officesReload, safeAction, setDisabled})
                )
            }
            <div className="tab-slides-container">
                {oMapToArr(TAB_COMPONENTS, (TabComponent, tabName) => (
                    <div key={tabName} className={cx("tab-slide", {active: tabName === activeTabName})}>
                        {TabComponent && (
                            <TabComponent
                                {...{branch, hasUnsavedChangesStatic}}
                                disabled={disabled || loading || !branch.branchActive}
                                onSave={async (saving) => {
                                    setDisabled(true);
                                    hasUnsavedChangesStatic.set(false);
                                    await saving;

                                    const updated = await reload();
                                    emitSave?.(updated);
                                    setDisabled(false);
                                }}
                                resetCallbackSetter={(reset) => setTabResetCallbacks({...getTabResetCallbacks(), [tabName]: reset})}
                            />
                        )}
                    </div>
                ))}
            </div>

            <button onClick={() => safeAction(emitClose)} className="close" xd-qa-type="icon" xd-qa-name="Default Modal Close">&times;</button>
        </div>
    ),
);

const headerDesktop = ({activeTabName, apis, branch, branchId, closeModal, disabled, emitTabSwitch, handleLinkClick, loading, officesReload, reload, safeAction, setDisabled}) => {
    return (
        <header>
            {AddressEdit({branch, reload, loading, officesReload, showHeader: true})}
            {navigation({activeTabName, branchId, closeModal, disabled, emitTabSwitch, handleLinkClick, safeAction})}
            {buttonDeactivate({apis, branch, branchId, disabled, loading, reload, safeAction, setDisabled})}
        </header>
    )
}

const headerMobile = ({activeTabName, apis, branch, branchId, closeModal, disabled, emitTabSwitch, handleLinkClick, loading, reload, officesReload, safeAction, setDisabled}) => {
    return (
        <>
            <header className="header-mobile">
                {AddressEdit({branch, reload, loading, officesReload, showHeader: true})}
            </header>
            {buttonDeactivate({apis, branch, branchId, disabled, loading, reload, safeAction, setDisabled})}
            {navigation({activeTabName, branchId, closeModal, disabled, emitTabSwitch, handleLinkClick, safeAction, showText: false})}
        </>
    )
}

const navigation = ({activeTabName, branchId, closeModal, disabled, emitTabSwitch, handleLinkClick, safeAction, showText = true}) => {
    return(
        <nav>
            {TAB_LINKS.map((tab) => (
                <a
                    key={tab.name}
                    onClick={() => !disabled && safeAction(() => emitTabSwitch(tab.name))}
                    className={cx({disabled, active: tab.name === activeTabName})}
                    xd-qa-type="button"
                    xd-qa-name={tab.label}
                >
                    <FontAwesomeIcon className="icon" icon={tab.icon}/>
                    {showText && tab.label}
                </a>
            ))}
            {
                <InternalLink
                    to={`/account/admin/user?office=${branchId}`}

                    onClick={handleLinkClick}
                    className={cx({disabled})}
                    xd-qa-type="button"
                    xd-qa-name="View Users"
                >
                    <FontAwesomeIcon className="icon" icon={faUsers} />
                    {showText && `View Users`}
                </InternalLink>
            }
            {window.location.pathname.indexOf('/office/') === -1 ? (
                <InternalLink
                    to={`/account/admin/office/address-lists?office=${branchId}`}
                    onClick={handleLinkClick}
                    className={cx({disabled})}
                    xd-qa-type="button"
                    xd-qa-name="Office Assets"
                >
                    <FontAwesomeIcon className="icon" icon={faArrowUpRightFromSquare}/>
                    {showText && `Office Assets`}
                </InternalLink>
            ) : (
                <a
                    onClick={() => handleLinkClick && closeModal()}
                    className={cx({disabled})}
                    xd-qa-type="button"
                    xd-qa-name="Office Assets"
                >
                    <FontAwesomeIcon className="icon" icon={faArrowUpRightFromSquare}/>
                    {showText && `Office Assets`}
                </a>
            )}
        </nav>
    )
}

const buttonDeactivate = ({apis, branch, branchId, disabled, loading, reload, safeAction, setDisabled}) => {
    return (
        <button
            disabled={disabled || loading}
            onClick={() => (
                safeAction(async () => {
                    setDisabled(true);
                    await apis.next.branch.updateBasicInfo({idBranch: branchId, branchActive: !branch.branchActive});
                    await reload();
                    setDisabled(false);
                })
            )}
            className={cx("pill", branch?.branchActive ? "deactivate" : "activate")}
            xd-qa-type="button"
            xd-qa-name={`${branch?.branchActive ? "Deactivate" : "Reactivate"} Office`}
        >
            {branch?.branchActive ? "Deactivate" : "Reactivate"} Office
        </button>
    )
}

